import { api, type DocumentMeta } from '../services/api';
import { resourcesApi, type Resource } from '../services/resources-api';
import { FilterBar, type FilterBarConfig, type FilterState } from './FilterBar';
import { UNIFIED_FILTERS, UNIFIED_ACTIONS } from '../config/filterbar-config';

export type InboxItemClickCallback = (docId: string) => void;

export class InboxView {
  private listEl: HTMLElement;
  private documents: DocumentMeta[] = [];
  private filterBar: FilterBar | null = null;
  private currentFilterState: FilterState | null = null;
  private onItemClick: InboxItemClickCallback | null = null;

  constructor() {
    this.listEl = document.getElementById('inbox-list')!;
    this.initFilterBar();
  }

  private initFilterBar(): void {
    const config: FilterBarConfig = {
      showSearch: true,
      showApplyButton: true,
      filters: UNIFIED_FILTERS,
      showSort: true,
      customActions: UNIFIED_ACTIONS,
      viewContext: 'inbox',
      title: 'Inbox',
      titleBadge: { count: 0, label: 'items' }
    };

    this.filterBar = new FilterBar('inbox-filter-bar', config);

    // Handle apply
    this.filterBar.onApplyCallback((state) => {
      this.currentFilterState = state;
      this.render();
    });

    // Handle search (live filtering)
    this.filterBar.onFilterChangeCallback((state) => {
      this.currentFilterState = state;
    });

    // Handle action buttons
    this.filterBar.onActionCallback((actionId) => {
      this.handleAction(actionId);
    });
  }

  private handleAction(actionId: string): void {
    switch (actionId) {
      case 'refresh':
        this.refresh();
        break;
      case 'add':
        if (this.onItemClick) {
          this.onItemClick('new');
        }
        break;
      case 'delete':
        // Delete handled at item level
        break;
    }
  }

  setOnItemClick(callback: InboxItemClickCallback): void {
    this.onItemClick = callback;
  }

  getItemById(id: string): DocumentMeta | undefined {
    return this.documents.find(d => d.id === id);
  }

  private readonly CACHE_KEY = 'inbox_documents_cache';

  async load(): Promise<void> {
    // 1. Show cached data immediately
    const cached = this.loadFromCache();
    if (cached) {
      this.documents = cached;
      this.updateUIFromDocuments();
      this.render();
    }

    // 2. Fetch fresh data in background
    await this.fetchFreshData(!cached);
  }

  private loadFromCache(): DocumentMeta[] | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;
      const { data } = JSON.parse(cached);
      return data;
    } catch {
      return null;
    }
  }

  private saveToCache(documents: DocumentMeta[]): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify({
        data: documents,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn('Failed to cache inbox:', e);
    }
  }

  private async fetchFreshData(showLoading: boolean = true): Promise<void> {
    try {
      // Fetch both documents and resources in parallel
      const [docsResponse, resourcesResponse] = await Promise.all([
        api.listDocuments(),
        resourcesApi.list({ limit: 200, parentId: null }) // Only parent resources, not extracted elements
      ]);

      const allItems: DocumentMeta[] = [];

      // Add documents (unassigned, not archived)
      if (docsResponse.success && docsResponse.data) {
        const docs = docsResponse.data.filter((doc: DocumentMeta) =>
          !doc.project && !doc.isArchived
        );
        allItems.push(...docs);
      }

      // Add resources (converted to DocumentMeta format)
      if (resourcesResponse.success && resourcesResponse.resources) {
        const resourcesAsDocs = resourcesResponse.resources.map((r: Resource) => ({
          id: r.id,
          title: r.title || 'Sin título',
          type: r.type === 'knowledge' ? 'Resource' : r.type,
          tags: r.tags || [],
          resourceCategory: r.resourceCategory,
          project: r.project,
          isArchived: false,
          isFavorite: false,
          isKnowledgeEnabled: r.isKnowledgeEnabled,
          updated_at: r.updatedAt,
          created_at: r.createdAt,
          version: 1,
          source: 'import' as const,
          customFields: r.customFields,
          // Mark as resource for UI differentiation
          _isResource: true,
          _sourceUrl: r.source || r.sourceUrl,
          _processingStatus: r.processingStatus,
          _humanReview: r.humanReview
        }));
        allItems.push(...resourcesAsDocs as DocumentMeta[]);
      }

      // Sort by created_at descending (newest first)
      allItems.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));

      const hasChanged = JSON.stringify(allItems.map(d => d.id).sort()) !==
                        JSON.stringify(this.documents.map(d => d.id).sort());

      this.documents = allItems;
      this.saveToCache(allItems);
      this.updateUIFromDocuments();

      if (hasChanged || showLoading) {
        this.render();
      }
    } catch (error) {
      console.error('Error loading inbox:', error);
    }
  }

  private updateUIFromDocuments(): void {
    // Update badge count
    if (this.filterBar) {
      this.filterBar.updateBadge(this.documents.length, 'Unassigned');
    }

    // Extract all unique tags for filter
    const tagsSet = new Set<string>();
    this.documents.forEach(doc => {
      doc.tags?.forEach(tag => tagsSet.add(tag));
    });
    const allTags = Array.from(tagsSet).sort();

    if (this.filterBar) {
      this.filterBar.setFilterOptions('tags', allTags.map(t => ({ value: t, label: t })));
    }
  }

  async refresh(): Promise<void> {
    await this.fetchFreshData(true);
  }

  private getFilteredDocuments(): DocumentMeta[] {
    let filtered = [...this.documents];
    const state = this.currentFilterState;

    if (!state) return filtered;

    // Filter by type
    const types = state.activeFilters.get('type');
    if (types && types.size > 0) {
      filtered = filtered.filter(doc => doc.type && types.has(doc.type));
    }

    // Filter by category
    const categories = state.activeFilters.get('category');
    if (categories && categories.size > 0) {
      filtered = filtered.filter(doc => doc.resourceCategory && categories.has(doc.resourceCategory));
    }

    // Filter by tags
    const tags = state.activeFilters.get('tags');
    if (tags && tags.size > 0) {
      filtered = filtered.filter(doc => {
        return doc.tags?.some(tag => tags.has(tag));
      });
    }

    // Filter by search
    if (state.search) {
      const query = state.search.toLowerCase();
      filtered = filtered.filter(doc => {
        const title = doc.title?.toLowerCase() || '';
        const summary = ((doc.customFields as Record<string, string>)?.summary || '').toLowerCase();
        const docTags = doc.tags?.join(' ').toLowerCase() || '';
        return title.includes(query) || summary.includes(query) || docTags.includes(query);
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (state.sortField === 'title') {
        aVal = a.title?.toLowerCase() || '';
        bVal = b.title?.toLowerCase() || '';
      } else {
        aVal = a[state.sortField as 'updated_at' | 'created_at'] || 0;
        bVal = b[state.sortField as 'updated_at' | 'created_at'] || 0;
      }

      if (state.sortDirection === 'desc') {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      } else {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
    });

    return filtered;
  }

  private render(): void {
    // Update sidebar badge
    const badge = document.getElementById('inbox-badge');
    if (badge) {
      if (this.documents.length > 0) {
        badge.textContent = this.documents.length.toString();
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    }

    const filtered = this.getFilteredDocuments();

    // Render list
    if (filtered.length === 0) {
      this.listEl.innerHTML = `
        <div class="inbox-empty">
          <span class="material-symbols-outlined inbox-empty-icon">inbox</span>
          <div class="inbox-empty-title">Inbox is empty</div>
          <div class="inbox-empty-text">Notes without a project will appear here</div>
        </div>
      `;
      return;
    }

    this.listEl.innerHTML = `<div class="inbox-list">${filtered.map(doc => this.renderCard(doc)).join('')}</div>`;

    // Attach click handlers
    this.listEl.querySelectorAll('.inbox-card').forEach(card => {
      const docId = card.getAttribute('data-id');
      if (docId) {
        card.addEventListener('click', (e) => {
          // Don't trigger if clicking action buttons
          if ((e.target as HTMLElement).closest('.inbox-card-actions')) return;
          if (this.onItemClick) {
            this.onItemClick(docId);
          }
        });
      }
    });

    // Attach action handlers
    this.listEl.querySelectorAll('.inbox-card-action[data-action="open"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const docId = (btn as HTMLElement).closest('.inbox-card')?.getAttribute('data-id');
        if (docId && this.onItemClick) {
          this.onItemClick(docId);
        }
      });
    });

    this.listEl.querySelectorAll('.inbox-card-action[data-action="delete"]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const card = (btn as HTMLElement).closest('.inbox-card');
        const docId = card?.getAttribute('data-id');
        const doc = this.documents.find(d => d.id === docId);
        if (docId && doc && confirm(`Delete "${doc.title}"?`)) {
          await api.deleteDocument(docId);
          card?.remove();
          this.documents = this.documents.filter(d => d.id !== docId);
          this.updateCount();
        }
      });
    });
  }

  private renderCard(doc: DocumentMeta): string {
    const timeAgo = this.formatTimeAgo(new Date(doc.updated_at));
    const snippet = this.getSnippet(doc);
    const tags = doc.tags || [];
    const customFields = doc.customFields as Record<string, string> | undefined;
    const url = customFields?.url || '';

    // Show mediaFormat (video, article, pdf) if available, otherwise fallback to type
    const mediaFormat = customFields?.mediaFormat;
    let typeLabel = 'Notes';
    if (mediaFormat) {
      // Capitalize first letter: video -> Video, article -> Article
      typeLabel = mediaFormat.charAt(0).toUpperCase() + mediaFormat.slice(1);
    } else if (doc.type === 'Resource' && doc.resourceCategory) {
      typeLabel = doc.resourceCategory;
    } else if (doc.type) {
      typeLabel = doc.type;
    }

    // External link icon if URL exists
    const externalLinkIcon = url ? `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer" class="inbox-card-external-link" title="Open link" onclick="event.stopPropagation();">
      <span class="material-symbols-outlined">open_in_new</span>
    </a>` : '';

    // Transcription badge
    const transcriptionBadge = this.getTranscriptionBadge(doc);

    return `
      <div class="inbox-card" data-id="${doc.id}">
        <div class="inbox-card-header">
          <div class="inbox-card-title-row">
            ${externalLinkIcon}<h3 class="inbox-card-title">${this.escapeHtml(doc.title || 'Untitled')}</h3>
          </div>
          <span class="inbox-card-time">${timeAgo}</span>
        </div>
        ${snippet ? `<p class="inbox-card-content">${this.escapeHtml(snippet)}</p>` : ''}
        <div class="inbox-card-footer">
          <div class="inbox-card-type">
            <span class="inbox-card-type-badge">${typeLabel}</span>
            ${transcriptionBadge}
          </div>
          <div class="inbox-card-tags">
            ${tags.map(tag => `<span class="inbox-card-tag">#${this.escapeHtml(tag)}</span>`).join('')}
          </div>
        </div>
        <div class="inbox-card-actions">
          <button class="inbox-card-action" data-action="open" title="Open">
            <span class="material-symbols-outlined">open_in_new</span>
          </button>
          <button class="inbox-card-action danger" data-action="delete" title="Delete">
            <span class="material-symbols-outlined">delete</span>
          </button>
        </div>
      </div>
    `;
  }

  private getSnippet(doc: DocumentMeta): string {
    const customFields = doc.customFields as Record<string, string> | undefined;
    if (customFields?.summary) {
      return customFields.summary.slice(0, 200);
    }
    return '';
  }

  private formatTimeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString();
  }

  private async archiveDocument(docId: string): Promise<void> {
    try {
      await api.updateDocument(docId, { isArchived: true });
    } catch (error) {
      console.error('Error archiving document:', error);
    }
  }

  private updateCount(): void {
    const count = this.documents.length;

    if (this.filterBar) {
      this.filterBar.updateBadge(count, 'Unassigned');
    }

    const badge = document.getElementById('inbox-badge');
    if (badge) {
      if (count > 0) {
        badge.textContent = count.toString();
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private getTranscriptionBadge(doc: DocumentMeta): string {
    const customFields = doc.customFields as Record<string, string> | undefined;
    const url = customFields?.url || '';
    const transcription = customFields?.transcription || '';
    const transcriptionStatus = customFields?.transcriptionStatus || '';

    // Determine status
    let status = 'disabled';
    if (transcriptionStatus === 'failed') {
      status = 'failed';
    } else if (transcriptionStatus === 'processing') {
      status = 'processing';
    } else if (transcription) {
      status = 'completed';
    } else if (this.isVideoUrl(url)) {
      status = 'available';
    }

    // Don't show badge for disabled state
    if (status === 'disabled') return '';

    return `<span class="kb-transcription-badge ${status}">
      <span class="material-symbols-outlined">closed_caption</span>
    </span>`;
  }

  private isVideoUrl(url: string): boolean {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('youtube.com') ||
           lower.includes('youtu.be') ||
           lower.includes('tiktok.com') ||
           lower.includes('instagram.com/reel') ||
           lower.includes('instagram.com/p/');
  }
}